import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles.filter((a) => a.slug !== slug && a.category === article?.category).slice(0, 3);
  const category = categories.find((c) => c.slug === article?.category);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 黑料入口 | 51吃瓜黑料入口一二三`;
    }
    window.scrollTo(0, 0);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">文章未找到</h1>
          <Link to="/" className="text-primary-500 text-sm mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <header>
        <Navbar />
      </header>

      <main className="pt-24 pb-16 bg-background-50">
        <article className="mx-auto px-4 md:px-6 max-w-[900px]">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            {category && (
              <>
                <Link to={`/category/${category.slug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">{category.name}</Link>
                <i className="ri-arrow-right-s-line"></i>
              </>
            )}
            <span className="text-foreground-800 line-clamp-1">{article.title}</span>
          </nav>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliaorukou.com/' },
                  ...(category
                    ? [{ '@type': 'ListItem', position: 2, name: category.name, item: `https://heiliaorukou.com/category/${category.slug}` }]
                    : []),
                  { '@type': 'ListItem', position: category ? 3 : 2, name: article.title },
                ],
              }),
            }}
          />

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: article.title,
                description: article.excerpt,
                datePublished: article.date,
                url: `https://heiliaorukou.com/article/${article.slug}`,
                image: article.image,
                author: {
                  '@type': 'Person',
                  name: '黑料入口编辑部',
                },
                publisher: {
                  '@type': 'Organization',
                  name: '黑料入口',
                  url: 'https://heiliaorukou.com/',
                },
              }),
            }}
          />

          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
              <div className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-sm"></i>
                <span>{article.date}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-eye-line text-sm"></i>
                <span>{article.views >= 10000 ? `${(article.views / 10000).toFixed(0)}万次阅读` : `${article.views}次阅读`}</span>
              </div>
              <span>黑料入口编辑部</span>
            </div>
          </div>

          <div className="w-full h-[350px] md:h-[450px] overflow-hidden rounded-lg mb-10 bg-background-100">
            <img
              src={article.image}
              alt={article.title}
              title={`黑料入口 ${article.title} - 黑料吃瓜在线观看`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-10">
            <div className="w-full lg:w-[65%]">
              <div className="prose-custom">
                {article.content.split('\n').map((paragraph, idx) => (
                  <p key={idx} className="text-base text-foreground-800 leading-loose mb-5">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="border-t border-background-200 mt-10 pt-8">
                <p className="text-xs text-foreground-500 mb-3">标签：</p>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="w-full lg:w-[35%]">
              <div className="bg-background-100 rounded-lg p-6 sticky top-24">
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">相关推荐</h3>
                {relatedArticles.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.slug}`}
                        className="group flex gap-3 cursor-pointer"
                      >
                        <div className="w-20 h-16 rounded-md overflow-hidden flex-shrink-0 bg-background-200">
                          <img
                            src={ra.image}
                            alt={ra.title}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                            {ra.title}
                          </h4>
                          <span className="text-xs text-foreground-500 mt-1 block">{ra.date}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-500">暂无相关文章</p>
                )}
                <Link
                  to="/"
                  className="inline-flex items-center gap-1 mt-6 text-xs font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  返回首页查看更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}